import { Typography } from "@optimizely/axiom";

import { ExperimentationProjectList } from "../ExperimentationProjectList/ExperimentationProjectList";
import { ProjectDisplayName } from "../ProjectDisplayName/ProjectDisplayName";

import { OrganizationProduct } from "../../../domain/OrganizationProduct";
import { Role } from "../../../domain/Role";

import { getFormattedDate } from "../../../lib/date-formatter";
import { NOT_FOUND } from "../../../constants/constants";

type RolePermissionsDetailsProps = {
    role: Role;
    scopes: (
        | {
              name: string;
              value: string;
          }
        | undefined
    )[];
    isExp?: boolean;
    instanceSelectionsText: string;
    products: OrganizationProduct[];
};

export const RolePermissionsDetails = ({
    role,
    scopes,
    isExp = false,
    instanceSelectionsText,
    products
}: RolePermissionsDetailsProps) => {
    const getProductName = (id: string) => {
        return products.find((p) => p.id === id)?.name || NOT_FOUND;
    };
    const getInstanceName = (id: string) => {
        return products.flatMap((prod) => prod.instances).find((i) => i.id === id)?.name || NOT_FOUND;
    };

    return (
        <>
            <span className="oui-label push-double--top">Instance</span>
            <Typography type="body" className="label--disabled">
                {instanceSelectionsText}
            </Typography>
            {role?.attributes.length > 0 && (
                <div>
                    <span className="oui-label push-double--top">Attributes</span>
                    <Typography type="body" className="label--disabled">
                        {role.attributes.map((attribute) => {
                            return !!attribute.values?.length ? (
                                <div key={attribute.id}>
                                    {attribute.key && attribute.values?.length ? (
                                        <strong>{attribute.key}:&nbsp;</strong>
                                    ) : (
                                        <span>{attribute.key}</span>
                                    )}
                                    {attribute.values.join(", ")}
                                </div>
                            ) : (
                                <div key={attribute.id}>
                                    <strong>{attribute.key}</strong>
                                </div>
                            );
                        })}
                    </Typography>
                    {scopes.length > 0 && (
                        <>
                            <span className="oui-label push-double--top">Restrictions</span>
                            <Typography type="body" className="label--disabled">
                                {scopes.map((scope) => {
                                    return (
                                        <>
                                            {scope?.name === "ProductId" && (
                                                <div>Product: {getProductName(scope?.value)}</div>
                                            )}
                                            {scope?.name === "InstanceId" && (
                                                <div>Instance: {getInstanceName(scope?.value)}</div>
                                            )}
                                            {scope?.name === "AttributeId" && scope?.value && (
                                                <div>
                                                    <span>Project: </span>
                                                    <ProjectDisplayName attributeId={scope?.value} />
                                                </div>
                                            )}
                                        </>
                                    );
                                })}
                            </Typography>
                        </>
                    )}
                </div>
            )}
            {isExp && <ExperimentationProjectList role={role} />}
            <span className="oui-label push-double--top">Created</span>
            <Typography type="body" className="label--disabled">
                {role?.created ? getFormattedDate({ date: role?.created, hideTimestamp: true }) : ""}
            </Typography>

            <span className="oui-label push-double--top">Modified</span>
            <Typography type="body" className="label--disabled">
                {role?.modified ? getFormattedDate({ date: role?.modified, hideTimestamp: true }) : ""}
            </Typography>
        </>
    );
};

RolePermissionsDetails.diplayName = "RolePermissionsDetails";
